import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  GraduationCap, 
  BrainCircuit, 
  Bus, 
  ShieldCheck, 
  Users, 
  BookOpen,
  LayoutDashboard,
  ArrowRight,
  CheckCircle2,
  Play,
  Menu,
  X,
  ChevronRight,
  Sparkles,
  Globe,
  Zap,
  Award,
  Clock,
  BarChart3,
  Wallet,
  Library,
  Bell
} from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const features = [
    {
      icon: BrainCircuit,
      title: "AI-Powered Tutoring",
      description: "Personalized learning paths powered by advanced AI that adapts to each student's pace and style.",
      span: "md:col-span-2",
      gradient: "from-violet-500 to-purple-600"
    },
    {
      icon: Bus,
      title: "Live Bus Tracking",
      description: "Real-time GPS tracking for school transportation with instant parent notifications.",
      span: "md:col-span-1",
      gradient: "from-emerald-500 to-teal-600"
    },
    {
      icon: ShieldCheck,
      title: "Global Compliance",
      description: "FERPA, COPPA & GDPR compliant. Enterprise-grade security for your institution.",
      span: "md:col-span-1",
      gradient: "from-blue-500 to-cyan-600"
    },
    {
      icon: Users,
      title: "Multi-Role Access",
      description: "Dedicated portals for Admins, Teachers, Students, Parents & Drivers - all seamlessly connected.",
      span: "md:col-span-2",
      gradient: "from-amber-500 to-orange-600"
    }
  ];

  const modules = [
    { icon: LayoutDashboard, name: "Dashboard Analytics", desc: "Real-time insights" },
    { icon: BookOpen, name: "Course Management", desc: "Curriculum & lessons" },
    { icon: Wallet, name: "Fee Management", desc: "Payments & invoices" },
    { icon: Library, name: "Digital Library", desc: "Resources & books" },
    { icon: Clock, name: "Attendance", desc: "Track & monitor" },
    { icon: BarChart3, name: "Exam Module", desc: "Tests & reports" },
    { icon: Bell, name: "Notifications", desc: "Instant alerts" },
    { icon: Award, name: "Achievements", desc: "Badges & rewards" }
  ];

  const stats = [
    { value: "500+", label: "Schools Trust Us" },
    { value: "1M+", label: "Active Students" },
    { value: "99.9%", label: "Uptime SLA" },
    { value: "50+", label: "Countries" }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className={`fixed top-14 md:top-16 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl shadow-sm" : "bg-transparent"
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Current ExpertLMS logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-950 to-indigo-800 rounded-xl flex items-center justify-center shadow-lg">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-indigo-950 tracking-tight">ExpertLMS</span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-indigo-950 font-medium transition-colors">Features</a>
              <a href="#modules" className="text-slate-600 hover:text-indigo-950 font-medium transition-colors">Modules</a>
              <a href="#pricing" className="text-slate-600 hover:text-indigo-950 font-medium transition-colors">Pricing</a>
              <a href="#contact" className="text-slate-600 hover:text-indigo-950 font-medium transition-colors">Contact</a>
            </div>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={() => navigate("/login")}
                className="text-indigo-950 hover:bg-indigo-50"
                data-testid="nav-login-btn"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => navigate("/login")}
                className="bg-indigo-950 hover:bg-indigo-900 text-white px-6 shadow-lg shadow-indigo-950/20"
                data-testid="nav-get-started-btn"
              >
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Mobile Menu Toggle */}
            <button 
              className="md:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 py-4 px-4 shadow-lg">
            <div className="flex flex-col gap-3">
              <a href="#features" className="py-2 px-4 text-slate-600 hover:bg-slate-50 rounded-lg">Features</a>
              <a href="#modules" className="py-2 px-4 text-slate-600 hover:bg-slate-50 rounded-lg">Modules</a>
              <a href="#pricing" className="py-2 px-4 text-slate-600 hover:bg-slate-50 rounded-lg">Pricing</a>
              <Button onClick={() => navigate("/login")} className="mt-2 bg-indigo-950">
                Sign In
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-indigo-50/50" />
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-gradient-to-br from-indigo-100/40 to-purple-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-emerald-100/30 to-cyan-100/30 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-full border border-indigo-100">
                <Sparkles className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-medium text-indigo-700">AI-Powered Education Platform</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-indigo-950 tracking-tight leading-[1.1]">
                The Future of
                <span className="block bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
                  Learning is Here
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-xl">
                ExpertLMS empowers schools, colleges, and universities with AI-driven tools, 
                seamless management, and enterprise-grade security trusted globally.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate("/login")}
                  className="bg-indigo-950 hover:bg-indigo-900 text-white px-8 py-6 text-lg shadow-xl shadow-indigo-950/20 transition-all hover:scale-105"
                  data-testid="hero-get-started-btn"
                >
                  Get Started Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-slate-200 hover:border-indigo-200 hover:bg-indigo-50 px-8 py-6 text-lg group"
                  data-testid="hero-demo-btn"
                >
                  <Play className="w-5 h-5 mr-2 text-indigo-600 group-hover:scale-110 transition-transform" />
                  Watch Demo
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-3">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 border-2 border-white" />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(i => (
                      <svg key={i} className="w-4 h-4 text-amber-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                      </svg>
                    ))}
                  </div>
                  <p className="text-sm text-slate-500">Trusted by 500+ institutions</p>
                </div>
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative lg:pl-8">
              <div className="relative">
                {/* Main Dashboard Preview */}
                <div className="relative bg-white rounded-2xl shadow-2xl shadow-indigo-950/10 overflow-hidden border border-slate-100">
                  <div className="h-8 bg-slate-50 flex items-center gap-2 px-4 border-b border-slate-100">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-amber-400" />
                    <div className="w-3 h-3 rounded-full bg-emerald-400" />
                  </div>
                  <img 
                    src="https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=800&q=80" 
                    alt="Dashboard Preview" 
                    className="w-full h-64 sm:h-80 object-cover"
                  />
                </div>

                {/* Floating Cards */}
                <div className="absolute -top-6 -right-6 bg-white/90 backdrop-blur-xl rounded-xl p-4 shadow-xl border border-white/50 animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">98% Pass Rate</p>
                      <p className="text-xs text-slate-500">Student Performance</p>
                    </div>
                  </div>
                </div>

                <div className="absolute -bottom-4 -left-4 bg-white/90 backdrop-blur-xl rounded-xl p-4 shadow-xl border border-white/50 animate-float-delayed">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Globe className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">50+ Countries</p>
                      <p className="text-xs text-slate-500">Global Reach</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-indigo-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-indigo-200 text-sm sm:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-32 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full border border-emerald-100 mb-6">
              <Zap className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">Powerful Features</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-indigo-950 mb-6">
              Everything You Need to
              <span className="block text-indigo-600">Transform Education</span>
            </h2>
            <p className="text-lg text-slate-600">
              A complete suite of tools designed to streamline operations, enhance learning, 
              and keep everyone connected.
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <Card 
                key={i} 
                className={`${feature.span} group hover:shadow-xl transition-all duration-500 border-0 bg-white overflow-hidden`}
              >
                <CardContent className="p-8 h-full">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-indigo-950 mb-3">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section id="modules" className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full border border-purple-100 mb-6">
                <LayoutDashboard className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-700">Complete Modules</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-indigo-950 mb-6">
                One Platform,
                <span className="block text-purple-600">Endless Possibilities</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                From admissions to alumni, ExpertLMS covers every aspect of educational 
                institution management with intuitive, powerful modules.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {modules.map((module, i) => (
                  <div key={i} className="flex items-center gap-3 p-4 bg-white rounded-xl hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <module.icon className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">{module.name}</p>
                      <p className="text-xs text-slate-500">{module.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-indigo-100 to-purple-100 rounded-3xl p-8">
                <img 
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80" 
                  alt="Students Learning" 
                  className="rounded-2xl shadow-2xl"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-xl max-w-xs">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">Industry Leading</p>
                    <p className="text-sm text-slate-500">Recognized by EdTech Digest as Top LMS 2025</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-indigo-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Institution?
          </h2>
          <p className="text-xl text-indigo-200 mb-10 max-w-2xl mx-auto">
            Join thousands of educational institutions worldwide who trust ExpertLMS 
            to deliver exceptional learning experiences.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate("/login")}
              className="bg-white text-indigo-950 hover:bg-slate-100 px-8 py-6 text-lg shadow-xl"
              data-testid="cta-get-started-btn"
            >
              Start Free Trial
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
              data-testid="cta-contact-btn"
            >
              Contact Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold">ExpertLMS</span>
              </div>
              <p className="text-slate-400 text-sm">
                Empowering education through technology.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#modules" className="hover:text-white transition-colors">Modules</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Updates</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">Privacy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms</a></li>
                <li><a href="#" className="hover:text-white transition-colors">FERPA</a></li>
                <li><a href="#" className="hover:text-white transition-colors">GDPR</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-sm text-slate-500">
            <p>&copy; {new Date().getFullYear()} ExpertLMS. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float 3s ease-in-out infinite;
          animation-delay: 1.5s;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
